import {
  createEmptyUtilityContractDetails,
  type UtilityContractDetails,
} from "../contexts/ContractContext";
import type { ContractCompany, ContractType } from "../services/contract-types";

const LOCAL_DRAFT_PREFIX = "newContractModalDraft";

export interface PersistentNewContractModalDraft {
  customerId: number;
  selectedCategory: string;
  selectedContractTypes: (ContractType & { company_details: ContractCompany })[];
  signature: string | null;
  bankDetails: string;
  birthDateText: string;
  useDateTextInput: boolean;
  gender: number | null;
  electricityDetails: UtilityContractDetails;
  gasDetails: UtilityContractDetails;
  searchQuery: string;
  currentPage: number;
  customerSignatureDataUrl: string | null;
}

type PersistedNewContractModalDraftStorage = {
  selectedCategory?: string;
  selectedContractTypes?: (ContractType & { company_details: ContractCompany })[];
  signature?: string | null;
  bankDetails?: string;
  birthDateText?: string;
  useDateTextInput?: boolean;
  gender?: number | null;
  electricityDetails?: Partial<UtilityContractDetails>;
  gasDetails?: Partial<UtilityContractDetails>;
  searchQuery?: string;
  currentPage?: number;
  customerSignatureDataUrl?: string | null;
};

const getDraftKey = (customerId: number): string =>
  `${LOCAL_DRAFT_PREFIX}:${customerId}`;

const canUseLocalStorage = (): boolean =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const normalizeUtilityDetails = (
  value: unknown
): UtilityContractDetails => {
  const defaults = createEmptyUtilityContractDetails();
  if (!value || typeof value !== "object") {
    return defaults;
  }

  const record = value as Partial<UtilityContractDetails>;
  return {
    previous_provider_name:
      typeof record.previous_provider_name === "string"
        ? record.previous_provider_name
        : defaults.previous_provider_name,
    previous_customer_number:
      typeof record.previous_customer_number === "string"
        ? record.previous_customer_number
        : defaults.previous_customer_number,
    annual_consumption_kwh:
      typeof record.annual_consumption_kwh === "string"
        ? record.annual_consumption_kwh
        : defaults.annual_consumption_kwh,
    meter_point_id:
      typeof record.meter_point_id === "string"
        ? record.meter_point_id
        : defaults.meter_point_id,
    meter_number:
      typeof record.meter_number === "string"
        ? record.meter_number
        : defaults.meter_number,
    meter_reading:
      typeof record.meter_reading === "string"
        ? record.meter_reading
        : defaults.meter_reading,
    unit_price_ct_kwh:
      typeof record.unit_price_ct_kwh === "string"
        ? record.unit_price_ct_kwh
        : defaults.unit_price_ct_kwh,
    base_price_eur_month:
      typeof record.base_price_eur_month === "string"
        ? record.base_price_eur_month
        : defaults.base_price_eur_month,
    delivery_start_date:
      typeof record.delivery_start_date === "string"
        ? record.delivery_start_date
        : defaults.delivery_start_date,
    as_soon_as_possible:
      typeof record.as_soon_as_possible === "boolean"
        ? record.as_soon_as_possible
        : defaults.as_soon_as_possible,
    desired_monthly_payment:
      typeof record.desired_monthly_payment === "string"
        ? record.desired_monthly_payment
        : defaults.desired_monthly_payment,
  };
};

export const getPersistentNewContractModalDraft = (
  customerId: number
): PersistentNewContractModalDraft | null => {
  if (!canUseLocalStorage()) return null;

  const raw = window.localStorage.getItem(getDraftKey(customerId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PersistedNewContractModalDraftStorage;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return {
      customerId,
      selectedCategory:
        typeof parsed.selectedCategory === "string"
          ? parsed.selectedCategory
          : "",
      selectedContractTypes: Array.isArray(parsed.selectedContractTypes)
        ? (parsed.selectedContractTypes as (ContractType & {
            company_details: ContractCompany;
          })[])
        : [],
      signature: typeof parsed.signature === "string" ? parsed.signature : null,
      bankDetails:
        typeof parsed.bankDetails === "string" ? parsed.bankDetails : "",
      birthDateText:
        typeof parsed.birthDateText === "string" ? parsed.birthDateText : "",
      useDateTextInput:
        typeof parsed.useDateTextInput === "boolean"
          ? parsed.useDateTextInput
          : false,
      gender: typeof parsed.gender === "number" ? parsed.gender : null,
      electricityDetails: normalizeUtilityDetails(parsed.electricityDetails),
      gasDetails: normalizeUtilityDetails(parsed.gasDetails),
      searchQuery:
        typeof parsed.searchQuery === "string" ? parsed.searchQuery : "",
      currentPage:
        typeof parsed.currentPage === "number" ? parsed.currentPage : 1,
      customerSignatureDataUrl:
        typeof parsed.customerSignatureDataUrl === "string"
          ? parsed.customerSignatureDataUrl
          : null,
    };
  } catch (error) {
    console.error("Could not parse persisted new contract modal draft", {
      customerId,
      error,
    });
    window.localStorage.removeItem(getDraftKey(customerId));
    return null;
  }
};

export const savePersistentNewContractModalDraft = (
  customerId: number,
  draft: PersistentNewContractModalDraft
): void => {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.setItem(getDraftKey(customerId), JSON.stringify(draft));
  } catch (error) {
    console.error("Could not persist new contract modal draft", {
      customerId,
      error,
    });
  }
};

export const clearPersistentNewContractModalDraft = (
  customerId: number
): void => {
  if (!canUseLocalStorage()) return;
  window.localStorage.removeItem(getDraftKey(customerId));
};
